import React, { useEffect, useState, useContext } from 'react';
import ApiClient from '../../Services/ApiClient';
import { useResources } from '../../store/resourcesProvider';
import AuthContext from '../../store/authContext';
import '../../style/footer.css';
import '../../style/base.css';

// Prevent accidental multiple footer renderings when the component is imported/used
// in more than one place. We use a global flag on window to ensure only one
// Footer instance renders at a time. The first mounted instance will render
// and mark ownership; subsequent instances will not render.
const Footer: React.FC = () => {
  // initialize visibility based on global flag to avoid flicker for extra instances
  const [visible] = React.useState<boolean>(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return !(typeof window !== 'undefined' && (window.__appHasFooter));
  });

  const ownerRef = React.useRef(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!window.__appHasFooter) {
      // claim ownership so future mounts won't render
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.__appHasFooter = true;
      ownerRef.current = true;
    }
    return () => {
      // release ownership when unmounting
      if (ownerRef.current && typeof window !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.__appHasFooter = false;
      }
    };
  }, []);

  if (!visible) return null;
  // load site settings (phone/email/services) so footer reflects per-site configuration
  const { siteId } = useResources();
  const auth = useContext(AuthContext);
  const isLoggedIn = auth?.isLoggedIn || false;
  const [phone, setPhone] = React.useState<string>('');
  const [email, setEmail] = React.useState<string>('');
  const [services, setServices] = React.useState<string[] | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const sid = siteId ? parseInt(siteId as string || '0', 10) : 0;
        const data: any = await ApiClient.get(`/api/sites/${sid}/site-settings`);
        if (!mounted) return;
        if (data) {
          setPhone(data.footerPhone || data.FooterPhone || '');
          setEmail(data.footerEmail || data.FooterEmail || '');
          setServices(data.footerServices || data.FooterServices || null);
        }
      } catch (e) {
        // ignore - fallback to defaults in UI
        console.debug('[Footer] failed to load site settings', e);
      }
    };
    load();
    return () => { mounted = false; };
  }, [siteId]);

  return (
    <footer className="items-footer rtl" role="contentinfo">
      <div className="footer-grid">
        <div>
          <h4>מידע</h4>
          <ul>
            <li><a href="/about">אודות</a></li>
            <li><a href="/contact">צור קשר</a></li>
            <li><a href="/accessibility">הצהרת נגישות</a></li>
          </ul>
        </div>
        <div>
          <h4>שירותים</h4>
          <ul>
            {services && services.length > 0 ? services.map((s, i) => <li key={i}>{s}</li>) : (
              <>
                {isLoggedIn && <li><a href="/items">פריטים</a></li>}
                {isLoggedIn && <li><a href="/resources">תוכן</a></li>}
              </>
            )}
          </ul>
        </div>
        <div>
          <h4>צור קשר</h4>
          <div>{email && email.length > 0 ? <a href={`mailto:${email}`}>{email}</a> : 'email@example.com'}</div>
          <div>{phone && phone.length > 0 ? phone : null}</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
